// frontend/user/src/components/upload-progress-popup.tsx
//
// 2026-05-28 重构:tasks 从 props 改为读 _lib/upload-store(zustand);
// onClear 改为调 store.clearDone(只清已完成,失败/进行中保留)。
// 所有任务完成 1.5s 后自动 onClose(只关 UI,不动 store 数据)。
//
// 2026-05-29:`data-frame-id="DH9xH"` 现在是 dangling reference —— pen `update ui context`
// (052a384b) 删除了 `DH9xH 页面-上传进度` frame。本 popup 沿用该 anchor 作为 e2e 锚点
// (不破坏既有 spec),但与 pen 设计稿已脱钩。详 docs/ui/user-page-flows/files.md `### DH9xH`。

import { useEffect, useMemo, useState } from 'react'
import { formatBytes, cn } from '@dogdisk/shared'
import { useUploadStore, type UploadTask } from '../_lib/upload-store'
import { formatSpeed } from '../_lib/upload-speed'

export type UploadProgressPopupProps = {
  onClose: () => void
}

export function UploadProgressPopup({ onClose }: UploadProgressPopupProps) {
  const tasks = useUploadStore((s) => s.tasks)
  const clearDone = useUploadStore((s) => s.clearDone)
  const [expanded, setExpanded] = useState(false)

  const stats = useMemo(() => {
    const active = tasks.filter((t) => t.status === 'uploading' || t.status === 'pending')
    const done = tasks.filter((t) => t.status === 'done')
    const failed = tasks.filter((t) => t.status === 'error')
    const totalBytes = tasks.reduce((acc, t) => acc + t.size, 0)
    const uploadedBytes = tasks.reduce((acc, t) => acc + t.bytesUploaded, 0)
    const overallPercent = totalBytes === 0 ? 0 : Math.round((uploadedBytes / totalBytes) * 100)
    return { active, done, failed, totalBytes, uploadedBytes, overallPercent }
  }, [tasks])

  // 全部任务完成(无 active)→ 1.5s 后自动收起 popup(只关 UI,store 数据保留)
  useEffect(() => {
    if (tasks.length === 0) return
    if (stats.active.length > 0) return
    const timer = setTimeout(() => onClose(), 1500)
    return () => clearTimeout(timer)
  }, [tasks.length, stats.active.length, onClose])

  if (tasks.length === 0) return null

  return (
    <div
      data-frame-id="DH9xH"
      role="dialog"
      aria-label="上传进度"
      className={cn(
        'fixed z-50 mx-auto max-w-[402px] transition-all',
        expanded
          ? 'inset-x-0 bottom-0 left-1/2 -translate-x-1/2 rounded-t-3xl bg-white shadow-2xl'
          : 'right-4 left-auto translate-x-0 rounded-2xl bg-white shadow-xl',
      )}
      style={
        expanded
          ? { paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }
          : { bottom: 'calc(140px + env(safe-area-inset-bottom))' }
      }
    >
      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left"
          aria-expanded="false"
        >
          <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#19caad]/10 text-[#19caad]">
            {stats.active.length > 0 ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
            ) : stats.failed.length > 0 ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="2.4"><path d="M6 6l12 12M18 6l-12 12" /></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12l5 5 9-12" /></svg>
            )}
          </span>
          <div className="flex-1">
            <p className="text-[13px] font-semibold text-[#1A1A1A]">
              {stats.active.length > 0
                ? `上传中 ${stats.done.length + 1} / ${tasks.length}`
                : stats.failed.length > 0
                  ? `${stats.failed.length} 个失败 · ${stats.done.length} 个完成`
                  : `已完成 ${tasks.length} 项`}
            </p>
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-[#F1F2F4]">
              <div
                className="h-full rounded-full bg-[#19caad] transition-all"
                style={{ width: `${stats.overallPercent}%` }}
              />
            </div>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
        </button>
      ) : (
        <div>
          <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
            <h2 className="text-[15px] font-semibold text-[#1A1A1A]">上传队列</h2>
            <div className="flex items-center gap-3 text-[12px]">
              {stats.active.length === 0 && stats.done.length > 0 && (
                <button type="button" onClick={clearDone} className="text-[#9CA3AF]">清空</button>
              )}
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="flex h-7 w-7 items-center justify-center rounded-full bg-[#F6F7F8] text-[#9CA3AF]"
                aria-label="收起"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 9l6 6 6-6" /></svg>
              </button>
            </div>
          </div>
          <ul className="max-h-[60vh] divide-y divide-[#F3F4F6] overflow-y-auto px-4">
            {tasks.map((t: UploadTask) => (
              <li key={t.id} className="flex flex-col gap-1.5 py-3 text-[13px]">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium text-[#1A1A1A]">{t.name}</span>
                  <span className="shrink-0 text-[11px] text-[#9CA3AF]">
                    {t.status === 'done'
                      ? '完成'
                      : t.status === 'error'
                        ? '失败'
                        : t.status === 'paused'
                          ? '已暂停'
                          : `${Math.round(t.percent)}%`}
                  </span>
                </div>
                {t.status === 'error' ? (
                  <p className="text-[12px] text-[#FF6B6B]">{t.error ?? '上传失败'}</p>
                ) : t.status === 'interrupted' ? (
                  <p className="text-[12px] text-[#FF6B6B]">已中断 — 在传输列表重选文件续传</p>
                ) : (
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#F1F2F4]">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        t.status === 'done' ? 'bg-[#19caad]' : 'bg-[#1971ca]',
                      )}
                      style={{ width: `${t.percent}%` }}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between text-[11px] text-[#9CA3AF]">
                  <span>{formatBytes(t.bytesUploaded)} / {formatBytes(t.size)}</span>
                  {t.status === 'uploading' && (
                    <span>
                      {t.phase === 'preparing'
                        ? `正在准备上传: ${Math.round(t.hashPercent ?? 0)}%`
                        : t.phase === 'finalizing'
                          ? '正在完成上传'
                          : formatSpeed(t.speedBps)}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
